import { useState } from "react";
import { IconEdit, IconListDetails, IconPlus, IconReportAnalytics, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, EmptyState, IconButton, PageHeader, Text } from "@/components/ui";
import { FilasTarjetaSkeleton } from "./ReporteriaAdminSkeletons";
import { qk } from "@/lib/query-keys";
import { useConfirmDialog } from "@/lib/confirm-dialog-store";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { eliminarConfiguracion, type ConfigResumen } from "../services/admin-reporteria.api";
import { useConfigsAdmin } from "../hooks/use-admin-reporteria";
import { ConfiguracionEditor } from "./ConfiguracionEditor";
import { PredefinidosManager } from "./PredefinidosManager";

export function AdminReporteriaPage() {
  const configs = useConfigsAdmin();
  const qc = useQueryClient();
  const confirm = useConfirmDialog();
  const [editar, setEditar] = useState<{ id: number | null } | null>(null);
  const [predefinidosDe, setPredefinidosDe] = useState<ConfigResumen | null>(null);

  const borrar = useMutation({
    mutationFn: eliminarConfiguracion,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.repAdminConfigs });
      toastExito({ title: "Configuración eliminada" });
    },
    onError: toastErrorApi,
  });

  async function confirmarEliminar(c: ConfigResumen) {
    const ok = await confirm({
      title: "Eliminar configuración",
      message: (
        <>
          ¿Eliminar <b>{c.nombre}</b>? Los usuarios que dependan solo de ella perderán el acceso a reportería.
        </>
      ),
      confirmLabel: "Eliminar",
      confirmColor: "peligro",
    });
    if (ok) borrar.mutate(c.id);
  }

  if (predefinidosDe) {
    return <PredefinidosManager config={predefinidosDe} onSalir={() => setPredefinidosDe(null)} />;
  }

  if (editar) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 md:px-6">
        <ConfiguracionEditor
          idConfig={editar.id}
          onSalir={() => {
            qc.invalidateQueries({ queryKey: qk.repAdminConfigs });
            setEditar(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 md:px-6">
      <PageHeader
        title="Reportería · Configuraciones"
        description="Define qué tablas y columnas puede reportar cada rol o usuario, el enmascarado de datos sensibles y las columnas calculadas de fábrica."
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditar({ id: null })}>
            Nueva configuración
          </Button>
        }
      />

      {configs.isLoading ? (
        <FilasTarjetaSkeleton botones={3} conBadge lineas={2} etiqueta="Cargando configuraciones…" />
      ) : !configs.data || configs.data.length === 0 ? (
        <EmptyState
          icon={IconReportAnalytics}
          title="Sin configuraciones"
          description="Crea la primera para habilitar el módulo de reportería a tus usuarios."
        />
      ) : (
        <div className="space-y-2">
          {configs.data.map((c) => (
            <Card key={c.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">{c.nombre}</span>
                  <Badge size="xs" variant="light">
                    {c.tipo === "rol" ? "Por rol" : "Por usuario"}
                  </Badge>
                  {!c.activo && (
                    <Badge size="xs" variant="light" className="!bg-muted !text-muted-foreground">
                      Inactiva
                    </Badge>
                  )}
                </div>
                {c.descripcion && (
                  <Text size="xs" c="dimmed" className="truncate">
                    {c.descripcion}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  {c.numTablas} tabla(s) · {c.numRoles} rol(es) · {c.numUsuarios} usuario(s)
                </Text>
              </div>
              <IconButton
                aria-label="Reportes predefinidos"
                title="Reportes predefinidos"
                variant="subtle"
                onClick={() => setPredefinidosDe(c)}
              >
                <IconListDetails size={16} />
              </IconButton>
              <IconButton aria-label="Editar" variant="light" onClick={() => setEditar({ id: c.id })}>
                <IconEdit size={16} />
              </IconButton>
              <IconButton aria-label="Eliminar" variant="subtle" onClick={() => confirmarEliminar(c)}>
                <IconTrash size={16} />
              </IconButton>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
